import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import {
	AppError,
	AuthenticationStatus,
	AuthSelector,
	JurisdictionSelector,
	MessageService,
	SessionSelector,
} from "@obg/ngx.core";
import { ignoreNil } from "@obg/rxjs";
import { isEmpty, isEqual, isUndefined, omitBy } from "lodash-es";
import { merge, of } from "rxjs";
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from "rxjs/operators";

import { CommonState } from "../state";

import { COMMON_CONTENT_ACTION_TYPE, CommonContentAction } from "./common-content.action";
import { commonContentMessageContext } from "./common-content.const";
import { CommonContent, CommonContentParams, CommonContentType } from "./common-content.model";
import { CommonContentSelector } from "./common-content.selector";
import { CommonContentService } from "./common-content.service";

@Injectable()
export class CommonContentEffect {
	getCommonContent$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload<CommonContentParams>>(COMMON_CONTENT_ACTION_TYPE.getCommonContent),
			map((x) => x.payload),
			mergeMap((params) =>
				this.store.select(AuthSelector.getAuthenticationStatus).pipe(
					filter((status) => status !== AuthenticationStatus.Pending),
					withLatestFrom(
						this.store.select(this.selector.getByParams(params)),
						this.store.select(this.sessionSelector.isAuthenticated()),
						this.store.pipe(select(JurisdictionSelector.get), ignoreNil())
					),
					switchMap(([, items, isAuthenticated, jurisdiction]) =>
						this.service
							.get(params, {
								isLoggedIn: isAuthenticated,
								jurisdiction,
							})
							.pipe(
								filter(
									(commonContent) =>
										!this.isCommonContentEqual(commonContent, items) || isEmpty(commonContent)
								),
								map((commonContent) =>
									this.action.getSuccess({
										commonContent,
										presenceType: params.presenceType,
										contentType: params.contentType,
									})
								),
								catchError((error: AppError) =>
									of(
										this.action.getFail({
											error,
											presenceType: params.presenceType,
											contentType: params.contentType,
										})
									)
								)
							)
					)
				)
			)
		)
	);

	getMainBanners$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload<string>>(COMMON_CONTENT_ACTION_TYPE.getCommonMainBanners),
			map((x) =>
				this.action.getCommonContent({
					contentType: CommonContentType.Main,
					presenceType: x.payload,
				})
			)
		)
	);

	getPromotionalBanners$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload<string>>(COMMON_CONTENT_ACTION_TYPE.getCommonPromotionalBanners),
			map((x) =>
				this.action.getCommonContent({
					contentType: CommonContentType.Promotional,
					presenceType: x.payload,
				})
			)
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[COMMON_CONTENT_ACTION_TYPE.getFail]: commonContentMessageContext.get,
			}),
			this.messageService.mapClearByContext$({
				[COMMON_CONTENT_ACTION_TYPE.getCommonContent]: commonContentMessageContext.get,
			})
		)
	);

	getSponsorshipBanner$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload<string>>(COMMON_CONTENT_ACTION_TYPE.getSponsorshipBanner),
			map((x) =>
				this.action.getCommonContent({
					contentType: CommonContentType.Banner,
					presenceType: x.payload,
				})
			)
		)
	);

	constructor(
		private actions$: Actions,
		private action: CommonContentAction,
		private selector: CommonContentSelector,
		private service: CommonContentService,
		private store: Store<CommonState>,
		private messageService: MessageService,
		private sessionSelector: SessionSelector
	) {}

	private isCommonContentEqual(prev: CommonContent[], next: CommonContent[]) {
		// In some occurrences values coming from SSR can be "undefined"
		return isEqual(
			(prev || []).map((f) => omitBy(f, isUndefined)),
			(next || []).map((f) => omitBy(f, isUndefined))
		);
	}
}
